import React, { useState } from 'react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { formatMacro } from '@shared/utils/formatting';
import { Flame, Utensils, ChevronDown, ChevronRight, Zap } from 'lucide-react';
import { useHubUIStore } from '../../../stores/v4/useHubUIStore';
import { DetailedMetric } from '@/shared/logic/metabolic/nutrition-compute';
import { SourceAuditList } from './SourceAuditList';

interface EnergyCommandCardProps {
    balance: number;
    consumed: {
        calories: DetailedMetric;
        protein: DetailedMetric;
        carbs: DetailedMetric;
        fat: DetailedMetric;
    };
    expended: {
        bmr: number;
        neat: number;
        training: number;
        total: number;
    };
}

/**
 * EnergyCommandCard: The unified tactical engine for daily fuel management.
 * V6.0: Enhanced with inline expandable source audits (SourceAuditList).
 */
export const EnergyCommandCard: React.FC<EnergyCommandCardProps> = ({ balance, consumed, expended }) => {
    const isCollapsed = useHubUIStore(state => state.collapsedSections['energy-balance'] || false);
    const toggleSection = useHubUIStore(state => state.toggleSection);

    const [expandedMacroKey, setExpandedMacroKey] = useState<string | null>(null);

    const isDeficit = balance >= 0;
    const accentColor = isDeficit ? '#2ecc71' : '#ef4444';

    const subHeaderStyle = { 
        fontSize: 9, 
        fontWeight: '900', 
        color: '#666', 
        letterSpacing: 1.5, 
        textTransform: 'uppercase',
        marginBottom: 8
    } as any;

    const metricGroupStyle = {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0'
    } as any;

    const numericStyle = { fontVariantNumeric: 'lining-nums', fontFamily: 'ui-sans-serif, system-ui, sans-serif' };

    const handleToggleAudit = (key: string) => {
        setExpandedMacroKey(expandedMacroKey === key ? null : key);
    };

    return (
        <Box style={{ 
            backgroundColor: 'rgba(255,255,255,0.03)', 
            borderBottomWidth: 1, 
            borderBottomColor: isCollapsed ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.08)',
            marginBottom: 15,
            overflow: 'hidden'
        }}>
            <Pressable 
                onPress={() => toggleSection('energy-balance')}
                style={{ 
                    padding: 20, 
                    display: 'flex', 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    backgroundColor: isCollapsed ? 'transparent' : 'rgba(255,255,255,0.01)'
                }}
            >
                <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <Zap size={14} color="#fff" />
                    <Typography style={{ fontSize: 11, fontWeight: '950', color: '#fff', letterSpacing: 1.5 }}>ENERGY BALANCE</Typography>
                </Box>

                <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    {isCollapsed && (
                        <Typography style={{ ...numericStyle, fontSize: 16, fontWeight: '950', color: accentColor, marginTop: 1 }}>
                            {isDeficit ? '' : '+'}{formatMacro(-balance)} KCAL
                        </Typography>
                    )}
                    {isCollapsed ? <ChevronRight size={18} color="#444" /> : <ChevronDown size={18} color="#666" />}
                </Box>
            </Pressable>

            {!isCollapsed && (
                <Box style={{ paddingHorizontal: 20, paddingBottom: 20 }}>
                    <Box style={{ paddingBottom: 15, marginBottom: 15 }}>
                        <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
                            <Typography style={{ ...numericStyle, fontSize: 22, fontWeight: '950', color: '#fff' }}>
                                {isDeficit ? '' : '+'}{formatMacro(-balance)}
                            </Typography>
                            <Typography style={{ fontSize: 12, fontWeight: '900', color: accentColor }}>
                                KCAL {isDeficit ? 'DEFICIT' : 'SURPLUS'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box style={{ marginBottom: 20, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)', paddingTop: 15 }}>
                        <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                            <Flame size={12} color="#f29b11" />
                            <Typography style={{ ...subHeaderStyle, marginBottom: 0, color: '#f29b11' }}>EXPENDITURE ENGINE</Typography>
                        </Box>
                        
                        <Box style={metricGroupStyle}>
                            <Box style={{ flex: 1 }}>
                                <Typography style={{ ...numericStyle, fontSize: 18, fontWeight: '900', color: '#fff' }}>{formatMacro(expended.total)}</Typography>
                                <Typography style={{ fontSize: 8, fontWeight: '800', color: '#444' }}>TOTAL TDEE</Typography>
                            </Box>
                            <Box style={{ display: 'flex', flexDirection: 'row', gap: 15, flex: 2, justifyContent: 'flex-end' }}>
                                <Box style={{ alignItems: 'flex-end' }}>
                                    <Typography style={{ ...numericStyle, fontSize: 14, fontWeight: '900', color: '#ccc' }}>{formatMacro(expended.bmr)}</Typography>
                                    <Typography style={{ fontSize: 7, fontWeight: '800', color: '#444' }}>BMR</Typography>
                                </Box>
                                <Box style={{ alignItems: 'flex-end' }}>
                                    <Typography style={{ ...numericStyle, fontSize: 14, fontWeight: '900', color: '#ccc' }}>{formatMacro(expended.neat)}</Typography>
                                    <Typography style={{ fontSize: 7, fontWeight: '800', color: '#444' }}>NEAT</Typography>
                                </Box>
                                <Box style={{ alignItems: 'flex-end' }}>
                                    <Typography style={{ ...numericStyle, fontSize: 14, fontWeight: '900', color: '#f29b11' }}>{formatMacro(expended.training)}</Typography>
                                    <Typography style={{ fontSize: 7, fontWeight: '800', color: '#444' }}>WORKOUT</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                            <Utensils size={12} color="#2ecc71" />
                            <Typography style={{ ...subHeaderStyle, marginBottom: 0, color: '#2ecc71' }}>INTAKE LEDGER</Typography>
                        </Box>

                        <Box style={metricGroupStyle}>
                            <Pressable onPress={() => handleToggleAudit('calories')} style={{ flex: 1 }}>
                                <Typography style={{ ...numericStyle, fontSize: 18, fontWeight: '900', color: '#fff' }}>{formatMacro(consumed.calories.total)}</Typography>
                                <Typography style={{ fontSize: 8, fontWeight: '800', color: '#444' }}>TOTAL KCAL</Typography>
                            </Pressable>
                            <Box style={{ display: 'flex', flexDirection: 'row', gap: 15, flex: 2, justifyContent: 'flex-end' }}>
                                <Pressable onPress={() => handleToggleAudit('protein')} style={{ alignItems: 'flex-end' }}>
                                    <Typography style={{ ...numericStyle, fontSize: 14, fontWeight: '900', color: '#3b82f6' }}>{formatMacro(consumed.protein.total)}g</Typography>
                                    <Typography style={{ fontSize: 7, fontWeight: '800', color: '#444' }}>PROTEIN</Typography>
                                </Pressable>
                                <Pressable onPress={() => handleToggleAudit('carbs')} style={{ alignItems: 'flex-end' }}>
                                    <Typography style={{ ...numericStyle, fontSize: 14, fontWeight: '900', color: '#f29b11' }}>{formatMacro(consumed.carbs.total)}g</Typography>
                                    <Typography style={{ fontSize: 7, fontWeight: '800', color: '#444' }}>CARBS</Typography>
                                </Pressable>
                                <Pressable onPress={() => handleToggleAudit('fat')} style={{ alignItems: 'flex-end' }}>
                                    <Typography style={{ ...numericStyle, fontSize: 14, fontWeight: '900', color: '#ef4444' }}>{formatMacro(consumed.fat.total)}g</Typography>
                                    <Typography style={{ fontSize: 7, fontWeight: '800', color: '#444' }}>FAT</Typography>
                                </Pressable>
                            </Box>
                        </Box>

                        {/* INLINE SOURCE AUDITS */}
                        <SourceAuditList sources={consumed.calories.sources} unit="kcal" color="#fff" isVisible={expandedMacroKey === 'calories'} />
                        <SourceAuditList sources={consumed.protein.sources} unit="g" color="#3b82f6" isVisible={expandedMacroKey === 'protein'} />
                        <SourceAuditList sources={consumed.carbs.sources} unit="g" color="#f29b11" isVisible={expandedMacroKey === 'carbs'} />
                        <SourceAuditList sources={consumed.fat.sources} unit="g" color="#ef4444" isVisible={expandedMacroKey === 'fat'} />
                    </Box>
                </Box>
            )}
        </Box>
    );
};
